import type { LivestreamService } from '../livestream/livestream.service';
import type { Membership, ProductShipmentPerk } from '../membership/models/membership.model';
import { MembershipHelper } from '../membership/services/membership.helper';
import { membershipService } from '../membership/services/membership.service';
import { isNonRenewingTerm } from '../membership/utils/membership.formatters';
import {
  getMembershipForProduct,
  getTermForProductVariant,
  getTierForProduct,
  isMembershipOrTierSoldOut,
} from '../membership/utils/membership.utlis';
import type { LineItemService } from '../shopify/line-item.service';
import type { Cart } from '../shopify/models/cart.model';
import type { Checkout } from '../shopify/models/checkout.model';
import type { LineItem } from '../shopify/models/line-item.model';
import type { LineItemList } from '../shopify/models/line-item-list.model';
import type { Product } from '../shopify/models/product.model';
import type { VariantAttribute } from '../shopify/models/variant-attribute.model';
import { shopifyClient } from '../shopify/shopify.client';
import type { AddToCartRequest, BuyItNowRequest } from './models/cart.model';
import { SoldOutItemError } from './models/cart.model';

export class CartService {
  constructor(
    private lineItemService: LineItemService,
    private livestreamService: LivestreamService
  ) {}
  async addToCart(
    request: AddToCartRequest,
    memberships?: Membership[],
    product?: Product,
    sections?: string[],
    sectionsUrl?: string
  ): Promise<LineItemList & { sections?: Record<string, string> }> {
    if (!request.livestreamId) {
      request.livestreamId = this.livestreamService.getLivestreamId();
    }
    if (request.membership && !!product) {
      const reloadedMemberships = await membershipService.reloadMembershipsIfNecessary(
        memberships,
        product.id?.toString(),
        request.productVariantId
      );

      return this.addToCartMembershipInternal(request, reloadedMemberships, product, sections, sectionsUrl);
    } else {
      const lineItem = this.lineItemService.buildLineItem({
        id: Number(request.productVariantId),
        quantity: request.quantity || 1,
        livestreamId: request.livestreamId,
        postId: request.postId,
        product,
        referralLinkId: request.referralLinkId,
        optInToMarketingEmails: request.optInToMarketingEmails,
        attributes: await this.lookupGatedVariantAttributes(product, request.productVariantId),
      });
      return this.addToCartInternal(lineItem, sections, sectionsUrl);
    }
  }

  async buyItNow(
    request: BuyItNowRequest,
    memberships?: any,
    product?: Product
  ): Promise<Checkout> {
    if (!request.livestreamId) {
      request.livestreamId = this.livestreamService.getLivestreamId();
    }
    if (request.membership && !!product) {
      const reloadedMemberships = await membershipService.reloadMembershipsIfNecessary(
        memberships,
        product.id?.toString(),
        request.productVariantId
      );

      return this.buyItNowMembershipInternal(request, reloadedMemberships, product);
    } else {
      const lineItem = this.lineItemService.buildBuyItNowLineItem({
        id: Number(request.productVariantId),
        quantity: 1,
        livestreamId: request.livestreamId,
        postId: request.postId,
        product,
        referralLinkId: request.referralLinkId,
        optInToMarketingEmails: request.optInToMarketingEmails,
        attributes: await this.lookupGatedVariantAttributes(product, request.productVariantId),
      });
      return shopifyClient.buyItNow([lineItem], request.accessToken);
    }
  }

  async getCart(): Promise<Cart> {
    return shopifyClient.getCart();
  }

  async updateCartProperties(id: number, properties: any): Promise<string> {
    return shopifyClient.updateCartProperties(id, properties);
  }

  private async lookupGatedVariantAttributes(
    product: Product | undefined,
    productVariantId: string | number
  ): Promise<VariantAttribute[] | undefined> {
    if (!product?.tags?.includes('single-gated')) {
      return undefined;
    }
    try {
      const attributes = await shopifyClient.lookupProductAttributes(
        Number(productVariantId),
        product.handle
      );
      return (attributes ?? []).filter((a) => a?.value !== '');
    } catch (err) {
      console.error('[CartService]', err);
      return undefined;
    }
  }

  private async addToCartInternal(
    lineItem: LineItem,
    sections?: string[],
    sectionsUrl?: string
  ): Promise<LineItemList & { sections?: Record<string, string> }> {
    return shopifyClient.addItemsToCart([lineItem], sections, sectionsUrl);
  }

  private async addToCartMembershipInternal(
    request: AddToCartRequest,
    memberships: Membership[],
    product: Product,
    sections?: string[],
    sectionsUrl?: string
  ): Promise<LineItemList & { sections?: Record<string, string> }> {
    const productId = product?.id?.toString();
    const membership = getMembershipForProduct(memberships, productId, request.productVariantId);
    const tier = getTierForProduct(memberships, productId, request.productVariantId);

    if (request.membership && isMembershipOrTierSoldOut(membership, tier)) {
      throw new SoldOutItemError(request.productVariantId);
    }

    const term = getTermForProductVariant(memberships, productId, request.productVariantId);
    // OTP (non-renewing) tiers have no selling plan; recurring tiers keep theirs.
    const isOneTimePurchase = isNonRenewingTerm(term);
    const perks = MembershipHelper.getActivePerksForPurchase(
      memberships,
      productId,
      request.productVariantId
    );

    if (request.membership && perks.length > 0) {
      // Fetch all perk products in parallel
      const perkProductPromises = perks.map(async (perk) => {
        try {
          const p = await shopifyClient.getProduct(perk.handle, true);
          const variant = p.variants.find((v) => v.id === Number(perk.productVariantId));

          if (!variant || !variant.available) {
            return null;
          }

          return {
            ...perk,
            attributes: variant.attributes,
          };
        } catch (err) {
          console.error('[ProductFormService]', err);
          return null;
        }
      });

      const mergedPerks = await Promise.all(perkProductPromises);

      // Build membership line item
      const membershipLineItem = this.lineItemService.buildLineItem({
        id: Number(request.productVariantId),
        quantity: 1,
        livestreamId: request.livestreamId,
        postId: request.postId,
        sellingPlanId: isOneTimePurchase ? undefined : Number(request.sellingPlanId),
        term,
        tier,
        product,
        attributes: product.variants.find((v) => v.id === Number(request.productVariantId))
          ?.attributes,
        optInToMarketingEmails: request.optInToMarketingEmails,
      } as LineItem);

      // Build perk line items
      const perkLineItems = mergedPerks
        .filter((perk) => perk !== null && !!perk.productVariantId)
        .map((perk) =>
          this.lineItemService.buildLineItem({
            id: Number(perk!.productVariantId),
            quantity: 1,
            livestreamId: request.livestreamId,
            postId: request.postId,
            perkMembershipVariantId: Number(request.productVariantId),
            attributes: perk!.attributes,
          })
        )
        .map((li) => this.lineItemService.appendLineItemProperties(membershipLineItem, li));

      const lineItems = [membershipLineItem, ...perkLineItems];

      if (!!lineItems && lineItems.length > 0) {
        return await shopifyClient.addItemsToCart(lineItems, sections, sectionsUrl);
      } else {
        return {} as LineItemList;
      }
    }

    // No perks - simple line item
    const lineItem = this.lineItemService.buildLineItem({
      id: Number(request.productVariantId),
      quantity: 1,
      livestreamId: request.livestreamId,
      postId: request.postId,
      sellingPlanId: isOneTimePurchase ? undefined : Number(request.sellingPlanId),
      term,
      tier,
      product,
      referralLinkId: request.referralLinkId,
      optInToMarketingEmails: request.optInToMarketingEmails,
    } as LineItem);

    return this.addToCartInternal(lineItem, sections, sectionsUrl);
  }

  private async buyItNowMembershipInternal(
    request: BuyItNowRequest,
    memberships: Membership[],
    product: Product
  ): Promise<Checkout> {
    const productId = product.id.toString();
    const term = getTermForProductVariant(memberships, productId, request.productVariantId);
    const tier = getTierForProduct(memberships, productId, request.productVariantId);
    // OTP (non-renewing) tiers have no selling plan; recurring tiers keep theirs.
    const isOneTimePurchase = isNonRenewingTerm(term);

    const membershipLineItem = this.lineItemService.buildBuyItNowLineItem({
      id: Number(request.productVariantId),
      quantity: 1,
      livestreamId: request.livestreamId,
      postId: request.postId,
      sellingPlanId: Number(request.sellingPlanId),
      term,
      tier,
      product,
      referralLinkId: request.referralLinkId,
      optInToMarketingEmails: request.optInToMarketingEmails,
    } as LineItem);

    const lineItems = [membershipLineItem];
    const perks = this.membershipService.getActivePerksForPurchase(
      memberships,
      productId,
      request.productVariantId
    );

    if (perks.length > 0) {
      const perkProductPromises = perks.map(async (perk) => {
        try {
          const p = await shopifyClient.getProduct(perk.handle, true);
          perk.attributes = p.variants.find(
            (v) => v.id === Number(perk.productVariantId)
          )?.attributes;
          return perk;
        } catch (err) {
          console.error('[ProductFormService]', err);
          return {} as ProductShipmentPerk;
        }
      });

      const mergedPerks = await Promise.all(perkProductPromises);

      const perkLineItems = mergedPerks
        .filter((perk) => !!perk && !!perk.productVariantId)
        .map((perk) =>
          this.lineItemService.buildBuyItNowLineItem({
            id: Number(perk.productVariantId),
            quantity: 1,
            livestreamId: request.livestreamId,
            postId: request.postId,
            perkMembershipVariantId: Number(request.productVariantId),
            attributes: perk.attributes,
          })
        )
        .map((li) => this.lineItemService.appendLineItemProperties(membershipLineItem, li));

      lineItems.push(...perkLineItems);
    }

    return await shopifyClient.buyItNow(lineItems, request.accessToken);
  }
}
